import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field, CccisdTextarea } from 'cccisd-formik';
import axios from 'cccisd-axios';
import notification from 'cccisd-notification';
import ClickButton from 'cccisd-click-button';
import Tooltip from 'cccisd-tooltip';
import format from 'date-fns/format';
import Modal from 'cccisd-modal';
import { Report, Page } from 'cccisd-laravel-report';
import Table from 'cccisd-table';
import IconNotebook from 'cccisd-icons/notebook';
import _get from 'lodash/get';
import style from './style.css';

const Boilerplate = window.cccisd.boilerplate;
const Fortress = window.cccisd.fortress;
const actingUser = window.cccisd.fortress.user.acting;

const Notes = ({ row, loadData }) => {
    const pawnId = row['pawn.pawnId'];
    const school = row.groupName;
    const notesData = row['fields.notes'];

    const notesModalRef = useRef();

    const notes = useMemo(() => {
        return notesData ? JSON.parse(notesData).sort((a, b) => b.createdAt.localeCompare(a.createdAt)) : [];
    }, [notesData]);

    const addNote = async values => {
        const newId = Math.max(...notes.map(item => item.id), 0) + 1;

        const newNotes = [
            ...notes,
            {
                id: newId,
                role: actingUser.role.label,
                note: values.note,
                createdAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                enteredBy: {
                    name: actingUser.user.full_name,
                    pawnId: Fortress.user.acting.id,
                    role: actingUser.role.label,
                },
            },
        ];

        await axios.put(Boilerplate.route('api.nexus.pawn.update', { pawn: pawnId }), {
            pawnHash: row['pawn.pawnHash'],
            notes: JSON.stringify(newNotes),
        });

        await loadData();

        notification('Note has been added.');
    };

    const columns = [
        {
            name: 'enteredBy',
            label: 'Entered By',
            class: 'text-nowrap',
            render: enteredBy => {
                return (
                    <div>
                        {_get(enteredBy, 'name', '')}{' '}
                        {_get(enteredBy, 'role', '') ? <div>{`(${_get(enteredBy, 'role', '')})`}</div> : ''}
                    </div>
                );
            },
        },
        {
            name: 'note',
            label: 'Note',
            render: note => {
                return <div style={{ wordBreak: 'break-word', minWidth: '320px' }}>{note}</div>;
            },
        },
        {
            name: 'createdAt',
            label: <div style={{ maxWidth: '120px' }}>Date</div>,
            class: 'text-nowrap',
        },
    ];

    const firstName = row['fields.firstName'];
    const lastName = row['fields.lastName'];
    const studentId = row['fields.studentId'];

    const getInfoBar = () => {
        return (
            <div className={style.top_info_bar}>
                <div className={style.bar_item}>
                    <b>Student:</b> {firstName + ' ' + lastName}
                </div>
                <div className={style.bar_item}>
                    <b>School:</b> {school}
                </div>
                {studentId && (
                    <div className={style.bar_item}>
                        <b>Student ID:</b> {studentId}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal
            title="Student Notes"
            size="large"
            contentStyle={{ minWidth: '65%', marginTop: '50px' }}
            beforeShow={loadData}
            ref={notesModalRef}
            renderTrigger={({ onClick }) => (
                <Tooltip
                    title={
                        notes.length > 0
                            ? `View, edit, or add notes for ${firstName} ${lastName}.`
                            : 'Add a note about the student or session.'
                    }
                >
                    <a
                        className="icon-link icon-link-hover"
                        style={{
                            '--bs-link-hover-color-rgb': '41, 74, 149',
                            textDecoration: 'underline',
                            textDecorationColor: '#a9bde7',
                        }}
                        onClick={e => {
                            e.preventDefault();
                            onClick();
                        }}
                        href="#"
                        data-notes-button
                    >
                        <IconNotebook />
                        {notes.length} Note{notes.length === 1 ? '' : 's'}
                    </a>
                </Tooltip>
            )}
        >
            <div className={style.notes_table_modal_body}>
                {getInfoBar()}
                <div className="d-flex gap-2">
                    <Modal
                        title="Add Student Note"
                        renderTrigger={({ onClick }) => (
                            <button type="button" className="btn btn-primary" onClick={onClick}>
                                Add Note
                            </button>
                        )}
                        contentStyle={{ minWidth: '55%', marginTop: '55px' }}
                        render={({ closeModal }) => (
                            <Formik
                                initialValues={{ note: '' }}
                                validate={values => {
                                    const errors = {};

                                    if (!('note' in values) || !values.note || !values.note.trim()) {
                                        errors.note = 'Note cannot be empty';
                                    }

                                    return errors;
                                }}
                                onSubmit={async values => {
                                    if (values.note) {
                                        values.note = values.note.trim();
                                    }

                                    await addNote(values);
                                    closeModal();
                                }}
                                render={({ isSubmitting, handleSubmit, values }) => (
                                    <>
                                        {getInfoBar()}
                                        <Form>
                                            <div>
                                                <Field
                                                    name="note"
                                                    component={CccisdTextarea}
                                                    label="Note"
                                                    rows={8}
                                                    maxLength={2000}
                                                    aria-describedby="remaining-characters"
                                                />
                                                <p
                                                    id="remaining-characters"
                                                    className="badge bg-light text-dark"
                                                >{`Characters remaining: ${2000 - values.note.length}`}</p>
                                                <br />
                                                <p className="badge bg-light text-dark" style={{ textAlign: 'left' }}>
                                                    Entered by <b>{actingUser.user.full_name}</b> {` on `}{' '}
                                                    {format(new Date(), 'yyyy-MM-dd')} <br />
                                                    <br />
                                                    Notes are not editable once added.
                                                </p>
                                            </div>
                                            <ClickButton
                                                className="btn btn-primary"
                                                type="submit"
                                                title="Submit"
                                                isLoading={isSubmitting}
                                                onClick={handleSubmit}
                                            />
                                        </Form>
                                    </>
                                )}
                            />
                        )}
                    />
                    {notes.length > 0 && (
                        <Modal
                            title="Notes"
                            renderTrigger={({ onClick }) => (
                                <button type="button" className="btn btn-secondary" onClick={onClick}>
                                    Export as PDF
                                </button>
                            )}
                            render={() => (
                                <Report>
                                    <Page>
                                        <div className={style.wrapper}>
                                            <div className={style.info_container}>
                                                <div className={style.student_info_container}>
                                                    <div className={style.student_labels_grid}>
                                                        <div className={style.student_labels_column1}>Student:</div>
                                                        <div className={style.student_labels_column2}>
                                                            {firstName + ' ' + lastName}
                                                        </div>

                                                        <div className={style.student_labels_column1}>School:</div>
                                                        <div className={style.student_labels_column2}>{school}</div>

                                                        {studentId && (
                                                            <>
                                                                <div className={style.student_labels_column1}>
                                                                    Student ID:
                                                                </div>

                                                                <div className={style.student_labels_column2}>
                                                                    {studentId}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={style.logo} />
                                        </div>
                                        <div className="fw-bold fs-2 mb-4">{`Student Notes for ${firstName} ${lastName}`}</div>
                                        <Table
                                            columns={columns}
                                            data={notes}
                                            showPerPageOptions={false}
                                            hideShowingRowsSummary
                                        />
                                    </Page>
                                </Report>
                            )}
                        />
                    )}
                </div>
                <div className="mt-3">
                    {notes.length > 0 && (
                        <Table columns={columns} data={notes} showPerPageOptions={false} hideShowingRowsSummary />
                    )}
                    {notes.length === 0 && (
                        <div style={{ justifyContent: 'center', display: 'flex' }}>No notes yet</div>
                    )}
                </div>
                <button
                    type="button"
                    className="btn btn-primary"
                    style={{ position: 'absolute', bottom: '0px', right: '0px' }}
                    onClick={() => {
                        notesModalRef.current.close();
                    }}
                >
                    Done
                </button>
            </div>
        </Modal>
    );
};

Notes.propTypes = {
    row: PropTypes.shape({
        groupName: PropTypes.string,
        'pawn.pawnId': PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        'pawn.pawnHash': PropTypes.string,
        'fields.firstName': PropTypes.string,
        'fields.lastName': PropTypes.string,
        'fields.notes': PropTypes.string,
    }),
    loadData: PropTypes.func,
};

Notes.defaultProps = {};

export default Notes;
